<script lang="ts" setup>
import {computed, onMounted, ref} from 'vue';
import {useSession} from '/src/core/session';
import router from '/src/router';
import Button from '/src/components/layout/Button.vue';
import {logEvent} from '/src/utils';
import PricingPlans from '../pricing/PricingPlans.vue';
import Icon from '/src/components/layout/Icon.vue';
import GalleryProjectsList from '../gallery/GalleryProjectsList.vue';
import {getGalleryEditorsChoice} from '/src/api/projects';

const session = useSession();

const auth = computed(() => session.state.auth);

const projects = ref([]);

async function getProjects() {
    let result;

    result = (await getGalleryEditorsChoice(0, 14)) ?? [];
    projects.value.push(...result);
}

onMounted(() => {
    if (auth.value) {
        router.push('/projects');
    } else {
        getProjects();
    }
});
</script>
<template>
    <div class="hero lg:pt-32 pt-12 lg:pb-32 pb-8 hero-bg-image">
        <div class="hero-content text-center">
            <div class="max-w-lg">
                <h1 class="lg:text-5xl text-3xl font-bold">
                    GUI design toolkit
                    <br />
                    for embedded screens
                </h1>
                <p class="py-6 text-lg">
                    Graphics editor and code builder for TFT_eSPI, U8G2, AdafruitGFX, ESPHome, Micropython, FlipperZero,
                    Inkplate, Watchy and more...
                </p>
                <router-link
                    to="/login"
                    @click="logEvent('button_sign_in', 'main')"
                >
                    <Button
                        success
                        variant=" h-12 px-4"
                    >
                        Get Started
                    </Button>
                </router-link>
            </div>
        </div>
    </div>
    <div class="lg:pb-16 pb-8 lg:w-2/3 w-full flex items-center justify-center mx-auto">
        <iframe
            class="hero-video rounded-xl xl:h-[600px] lg:h-[500px] md:h-[400px] sm:h-[320px] h-[200px]"
            width="100%"
            src="https://www.youtube.com/embed/fRtTuNN2NPY"
            title="YouTube video player"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerpolicy="strict-origin-when-cross-origin"
            allowfullscreen
        ></iframe>
    </div>
    <div class="w-full py-10 pb-20 text-center">
        <div class="text-lg mb-6">Trusted by makers, engineers, and hobbyists worldwide</div>
        <div class="flex flex-wrap items-center content-center gap-4 brightness-50">
            <img
                src="/assets/img/platforms/arduino.png"
                class="h-6 lg:h-12 object-contain mx-auto"
            />
            <img
                src="/assets/img/platforms/flipper.png"
                class="h-3 lg:h-5 object-contain mx-auto"
            />
            <img
                src="/assets/img/platforms/esphome.png"
                class="h-6 lg:h-12 object-contain mx-auto"
            />
            <img
                src="/assets/img/platforms/micropython.png"
                class="h-6 lg:h-12 object-contain mx-auto"
            />
            <img
                src="/assets/img/platforms/espressif.png"
                class="h-6 lg:h-10 object-contain mx-auto"
            />
            <img
                src="/assets/img/platforms/adafruit.png"
                class="h-6 lg:h-12 object-contain mx-auto"
            />
            <img
                src="/assets/img/platforms/raspberry.png"
                class="h-6 lg:h-14 object-contain mx-auto"
            />
            <!-- <img
            src="/assets/img/platforms/soldered.png"
            class="h-6 lg:h-12 object-contain mx-auto"
            /> -->
            <img
                src="/assets/img/platforms/u8g2.png"
                class="h-6 lg:h-12 object-contain mx-auto"
            />
        </div>
    </div>
    <div class="w-full py-16 flex flex-col items-center">
        <h2 class="text-4xl font-bold pb-4 text-center">
            Stop Building UIs the
            <span class="text-error">Hard Way</span>
        </h2>
        <div class="text-lg pb-12 text-center max-w-2xl mx-auto">
            Traditional embedded UI development is broken. Here's how we're fixing it.
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-5xl">
            <div class="rounded-2xl bg-base-200/90 p-8 flex flex-col gap-4 shadow-lg border border-base-300 relative">
                <div class="flex items-center gap-2 mb-2">
                    <span class="badge badge-error badge-outline font-bold">The Old Way</span>
                    <Icon
                        type="shield"
                        class="w-6 h-6 text-error"
                    />
                </div>
                <div class="text-2xl font-bold text-white mb-4">The Pain of Embedded UI Development</div>
                <ul class="flex flex-col gap-4 text-base-content/80">
                    <li class="flex items-start gap-3">
                        <Icon
                            type="paint"
                            class="w-6 h-6 text-error mt-1"
                        />
                        <span>
                            <span class="font-semibold text-white">UI built by hand</span>
                            <br />
                            Code, compile, test, repeat — endless cycles of frustration
                        </span>
                    </li>
                    <li class="flex items-start gap-3">
                        <Icon
                            type="wrench-screw"
                            class="w-6 h-6 text-error mt-1"
                        />
                        <span>
                            <span class="font-semibold text-white">Endless pixel counting</span>
                            <br />
                            Measure pixel by pixel, pixel by pixel, pixel by pixel...
                        </span>
                    </li>
                    <li class="flex items-start gap-3">
                        <Icon
                            type="rect-group"
                            class="w-6 h-6 text-error mt-1"
                        />
                        <span>
                            <span class="font-semibold text-white">Fragmented tools, messy workflows</span>
                            <br />
                            Inconsistent results across different platforms and devices
                        </span>
                    </li>
                    <li class="flex items-start gap-3">
                        <Icon
                            type="clip"
                            class="w-6 h-6 text-error mt-1"
                        />
                        <span>
                            <span class="font-semibold text-white">Tedious asset imports</span>
                            <br />
                            Manual tweaks for every screen, no consistency
                        </span>
                    </li>
                    <li class="flex items-start gap-3">
                        <Icon
                            type="eye-slash"
                            class="w-6 h-6 text-error mt-1"
                        />
                        <span>
                            <span class="font-semibold text-white">Polished UIs feel impossible</span>
                            <br />
                            Microcontrollers seem limited to basic, ugly interfaces
                        </span>
                    </li>
                </ul>
            </div>
            <div class="rounded-2xl bg-orange-500 p-8 flex flex-col gap-4 shadow-lg border border-orange-700 relative">
                <div class="flex items-center gap-2 mb-2">
                    <span class="badge badge-success font-bold">The New Way</span>
                    <Icon
                        type="bolt"
                        class="w-6 h-6 text-neutral-900"
                    />
                </div>
                <div class="text-2xl font-bold text-neutral-900 mb-4">The Lopaka Way of UI Design</div>
                <ul class="flex flex-col gap-4 text-neutral-900/90">
                    <li class="flex items-start gap-3">
                        <Icon
                            type="paint"
                            class="w-6 h-6 text-neutral-900 mt-1"
                        />
                        <span>
                            <span class="font-semibold text-neutral-900">Visual drag-and-drop editor</span>
                            <br />
                            Real-time preview, design at the speed of thought
                        </span>
                    </li>
                    <li class="flex items-start gap-3">
                        <Icon
                            type="globe"
                            class="w-6 h-6 text-neutral-900 mt-1"
                        />
                        <span>
                            <span class="font-semibold text-neutral-900">Pixel perfect precision</span>
                            <br />
                            What you see in the editor is what appears on your device
                        </span>
                    </li>
                    <li class="flex items-start gap-3">
                        <Icon
                            type="clone"
                            class="w-6 h-6 text-neutral-900 mt-1"
                        />
                        <span>
                            <span class="font-semibold text-neutral-900">Instantly export deployable source code</span>
                            <br />
                            Production-ready C, C++, Python code in seconds
                        </span>
                    </li>
                    <li class="flex items-start gap-3">
                        <Icon
                            type="clipboard"
                            class="w-6 h-6 text-neutral-900 mt-1"
                        />
                        <span>
                            <span class="font-semibold text-neutral-900">Seamless asset management</span>
                            <br />
                            Easy iterations, consistent results across all your projects
                        </span>
                    </li>
                    <li class="flex items-start gap-3">
                        <Icon
                            type="star"
                            class="w-6 h-6 text-neutral-900 mt-1"
                        />
                        <span>
                            <span class="font-semibold text-neutral-900">Professional UIs on low-power hardware</span>
                            <br />
                            Fast, beautiful, and fun — even on microcontrollers
                        </span>
                    </li>
                </ul>
            </div>
        </div>
        <div class="w-full flex flex-col items-center mt-12">
            <div class="text-base-content/80 text-lg mb-4">Ready to transform your embedded UI workflow?</div>
            <router-link
                to="/login"
                @click="logEvent('button_sign_in', 'main')"
            >
                <Button
                    success
                    variant=" h-12 px-4"
                >
                    Try For Free
                </Button>
            </router-link>
        </div>
    </div>
    <div class="w-full py-16 bg-base-100 flex flex-col items-center">
        <h2 class="text-4xl font-bold pb-12 text-center">Features</h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
            <div class="card bg-base-200 shadow-md">
                <div class="card-body">
                    <h3 class="card-title">Pixel Perfect Editor</h3>
                    <p>Design with confidence — what you see in the editor is exactly what appears on your device.</p>
                </div>
            </div>
            <div class="card bg-base-200 shadow-md">
                <div class="card-body">
                    <h3 class="card-title">Auto-Generate XBMP Graphics</h3>
                    <p>Effortlessly convert artwork into efficient XBMP graphics, ready for embedded use.</p>
                </div>
            </div>
            <div class="card bg-base-200 shadow-md">
                <div class="card-body">
                    <h3 class="card-title">Font Converter</h3>
                    <p>
                        Import and convert TTF, OTF, WOFF, BDF, and GFX fonts for your project. Enjoy more creative
                        freedom and better code readability.
                    </p>
                </div>
            </div>
            <div class="card bg-base-200 shadow-md">
                <!-- <div class="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <span class="badge badge-warning text-xs px-3 py-1 font-bold">EXTRA</span>
                </div> -->
                <div class="card-body">
                    <h3 class="card-title">Drawing Shapes and Tools</h3>
                    <p>
                        Unleash creativity with a rich set of drawing tools, from rectangles and ellipses to freehand
                        sketches.
                    </p>
                </div>
            </div>
            <div class="card bg-base-200 shadow-md">
                <div class="card-body">
                    <h3 class="card-title">Popular Fonts Support</h3>
                    <p>Access a curated library of fonts for expressive, readable text on any platform.</p>
                </div>
            </div>
            <div class="card bg-base-200 shadow-md">
                <div class="card-body">
                    <h3 class="card-title">Use Custom Images</h3>
                    <p>Import your own images to personalize and enhance your designs.</p>
                </div>
            </div>
            <div class="card bg-base-200 shadow-md">
                <div class="card-body">
                    <h3 class="card-title">Variables</h3>
                    <p>
                        Declare variables for any layer property — position, size, color, and text. For cleaner, more
                        flexible code.
                    </p>
                </div>
            </div>
            <!-- <div class="card bg-base-200 shadow-md">
                <div class="card-body">
                    <h3 class="card-title">Move, Resize, Edit Elements</h3>
                    <p>Arrange, tweak, and fine-tune every element with intuitive controls.</p>
                </div>
            </div> -->
            <div class="card bg-base-200 shadow-md">
                <div class="card-body">
                    <h3 class="card-title">Generate code for any platform</h3>
                    <p>Export graphics for U8g2, AdafruitGFX, TFT_eSPI, and more — no more pixel hunting.</p>
                </div>
            </div>
            <!-- <div class="card bg-base-200 shadow-md">
                <div class="card-body">
                    <h3 class="card-title">FlipperZero Live Preview</h3>
                    <p>
                        See real-time previews for FlipperZero, ensuring your graphics look perfect before deployment.
                    </p>
                </div>
            </div> -->
        </div>
    </div>
    <div class="w-full py-16 flex flex-col items-center">
        <h2 class="text-4xl font-bold pb-4 text-center">
            A full
            <router-link
                to="/gallery"
                class="link link-primary"
            >
                <b>Gallery</b>
            </router-link>
            of templates
        </h2>
        <div class="text-lg pb-12 text-center max-w-2xl mx-auto">
            Made by our community, for our community. Handpicked by our team.
        </div>
        <GalleryProjectsList :projects="projects" />
    </div>
    <div class="lg:pb-16 py-8 2xl:w-3/4 w-full mx-auto">
        <h2 class="text-3xl pb-4 font-bold text-center">Pricing Plans</h2>
        <PricingPlans></PricingPlans>
    </div>
</template>
<style lang="css" scoped>
.hero-bg-image {
    background-image: url('/assets/img/bg-screens.jpg');
    background-position: center;
    box-shadow: inset 0 0 100px 100px #0a0a0a;
}
.hero-video {
    box-shadow: 0 0 100px 1px oklch(var(--p));
}
</style>
